import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton,
  Stack, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from "@mui/material";
import { Close, Refresh, Delete, Save, Wifi, WifiOff } from "@mui/icons-material";
import config from "../../Util/config";
import { DeviceSession } from "../../Model/DeviceSession";
import { formatDate } from "../../Util/timeConverter";

type LogItem = {
  id: number;
  displayName?: string;
  eventType?: string;
  severity?: string;
  message?: string;
  timestamp?: string;
};

type Props = {
  device: DeviceSession | null;
  open: boolean;
  onClose: () => void;
  onForceUpdate: (device: DeviceSession) => void;
  onDelete: (device: DeviceSession) => void;
  onRename: (id: number, name: string) => void;
};

const REFRESH_MS = 10000;
const LOG_LIMIT = 300;

function formatUptime(seconds?: number): string {
  if (seconds == null) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function severityColor(sev?: string): string {
  switch ((sev || "").toUpperCase()) {
    case "ERROR": return "#ef4444";
    case "WARNING": return "#f59e0b";
    case "SUCCESS": return "#22c55e";
    default: return "#94a3b8";
  }
}

const InfoCell = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{value ?? "—"}</Typography>
  </Box>
);

const DeviceDetailsModal = ({ device, open, onClose, onForceUpdate, onDelete, onRename }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const deviceCode = device?.deviceCode;

  const fetchLogs = useCallback(async () => {
    if (!deviceCode) { setLogs([]); return; }
    try {
      setLoadingLogs(true);
      const res = await fetch(`${config.API.BASE_URL}/display-log/recent?limit=${LOG_LIMIT}`);
      if (!res.ok) return;
      const data: LogItem[] = await res.json();
      setLogs(data.filter((l) => l.displayName === deviceCode));
    } catch {
      /* silencioso */
    } finally {
      setLoadingLogs(false);
    }
  }, [deviceCode]);

  useEffect(() => {
    if (!open) return;
    setNameDraft(device?.name ?? "");
    fetchLogs();
    timerRef.current = setInterval(fetchLogs, REFRESH_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open, device?.name, fetchLogs]);

  if (!device) return null;

  const canForceUpdate = device.status && !!device.deviceCode;
  const nameChanged = nameDraft.trim() !== "" && nameDraft !== (device.name ?? "");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        {device.status
          ? <Wifi color="success" fontSize="small" />
          : <WifiOff color="error" fontSize="small" />}
        <Typography sx={{ fontSize: 18, fontWeight: 700, flex: 1 }}>
          {device.name || device.model || "Dispositivo"}
        </Typography>
        <Chip
          label={device.status ? "Online" : "Offline"}
          size="small"
          color={device.status ? "success" : "default"}
        />
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Apelido editável */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
          <TextField
            label="Apelido"
            size="small"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            sx={{ flex: 1, maxWidth: 360 }}
          />
          <Button
            variant="contained"
            startIcon={<Save />}
            disabled={!nameChanged}
            onClick={() => onRename(device.id, nameDraft.trim())}
          >
            Salvar
          </Button>
        </Stack>

        {/* Grid de informações */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mb: 2 }}>
          <InfoCell label="Modelo" value={device.model} />
          <InfoCell label="Android" value={device.androidVersion} />
          <InfoCell label="App" value={device.appVersion} />
          <InfoCell label="RAM livre" value={
            device.ramFreeMb != null
              ? device.ramFreeMb > 32768
                ? `${Math.round(device.ramFreeMb / 1024)} MB`
                : `${device.ramFreeMb} MB`
              : "—"
          } />
          <InfoCell label="Ativo há" value={formatUptime(device.uptimeSeconds)} />
          <InfoCell label="Reinícios" value={device.startCount ?? "—"} />
          <InfoCell label="Playlist" value={device.playlist?.name} />
          <InfoCell label="Código" value={device.deviceCode} />
          <InfoCell label="Último sinal" value={formatDate(device.lastSeen)} />
        </Box>

        {/* Ações */}
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Tooltip title={canForceUpdate ? "" : "Só para TVs online com código registrado"}>
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Refresh />}
                disabled={!canForceUpdate}
                onClick={() => onForceUpdate(device)}
              >
                Forçar Atualização
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" color="error" startIcon={<Delete />} onClick={() => onDelete(device)}>
            Excluir
          </Button>
        </Stack>

        <Divider sx={{ mb: 1 }} />

        {/* Logs ao vivo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Logs ao vivo</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            (atualiza a cada {REFRESH_MS / 1000}s)
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={fetchLogs} disabled={loadingLogs}>
            <Refresh fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            bgcolor: "#0b1220", color: "#cbd5e1", borderRadius: 2, p: 1.5,
            height: 300, overflowY: "auto", fontFamily: "monospace", fontSize: 12.5,
          }}
        >
          {!deviceCode && (
            <Typography sx={{ color: "#f59e0b", fontFamily: "monospace", fontSize: 12.5 }}>
              Esta TV ainda não registrou um código (sem logs).
            </Typography>
          )}
          {deviceCode && logs.length === 0 && (
            <Typography sx={{ color: "#94a3b8", fontFamily: "monospace", fontSize: 12.5 }}>
              Sem logs recentes desta TV.
            </Typography>
          )}
          {logs.map((l) => (
            <Box key={l.id} sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
              <span style={{ color: "#64748b" }}>{l.timestamp?.slice(11, 19)} </span>
              <span style={{ color: severityColor(l.severity), fontWeight: 700 }}>
                [{l.eventType}]
              </span>{" "}
              <span>{l.message}</span>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceDetailsModal;
